/**
 * The studio's LOOK FIELDS — every value Basic mode nudges and AI Look Match
 * may set, in one list.
 *
 * Pure data with no imports, because two programs read it:
 *   - the browser (`src/ui/editor/basic/`), which maps each key to the real
 *     Advanced row or material RPC it writes through;
 *   - the API server (`server/modules/ai/`), which builds the model's prompt
 *     and output schema from it.
 * A key that exists here but not in the browser's target map is a build
 * failure (`scripts/check-basic-macros.ts`), so the two cannot drift.
 *
 * `lo`/`hi` is the GOOD-LOOKING band, not the slider's full range: Basic mode
 * never pushes past it and the AI is clamped to it. The check script proves
 * every band sits inside its slider's own min/max.
 *
 * `description` is written for the vision model — what the value does to the
 * PICTURE, in the words a photographer would use — not for the UI.
 */

export type LookCategory = "diamond" | "metal" | "reflections" | "shadow" | "light";

export type LookFieldType = "number" | "boolean" | "color";

export interface LookField {
  key: string;
  category: LookCategory;
  label: string;
  type: LookFieldType;
  /** Good-looking band (numbers only). */
  lo?: number;
  hi?: number;
  /** Integer-valued (e.g. ray bounces). */
  integer?: boolean;
  description: string;
}

export const LOOK_CATEGORIES: { id: LookCategory; label: string }[] = [
  { id: "diamond", label: "Diamond" },
  { id: "metal", label: "Metal" },
  { id: "reflections", label: "Reflections" },
  { id: "shadow", label: "Ground & shadow" },
  { id: "light", label: "Light & background" },
];

export const LOOK_FIELDS: LookField[] = [
  // ── Diamond (applies to every gem part) ──────────────────────────────────
  {
    key: "gem.gammaFactor", category: "diamond", label: "Facet contrast", type: "number", lo: 0.7, hi: 1.8,
    description: "Contrast between bright and dark facets inside the stone. Higher = crisper, more graphic facet pattern with deeper blacks; lower = flatter, milky, low-contrast stone.",
  },
  {
    key: "gem.bounces", category: "diamond", label: "Inner reflections", type: "number", lo: 3, hi: 10, integer: true,
    description: "How many internal reflections are traced. More = more small facet reflections visible inside the stone and a busier, livelier pattern; fewer = simpler, darker interior.",
  },
  {
    key: "gem.sparkleBoost", category: "diamond", label: "Sparkle", type: "number", lo: 0.9, hi: 1.26,
    description: "Scrambles the light leaving the stone. Higher = more fragmented, scintillating sparkle; 1 = physically neutral.",
  },
  {
    key: "gem.dispersion", category: "diamond", label: "Fire", type: "number", lo: 0, hi: 0.12,
    description: "Rainbow colour splitting (fire) on facet edges. 0 = no colour flashes; 0.01 = a real white diamond; 0.04+ = strong rainbow fire; 0.1+ = vivid, stylised rainbow.",
  },
  {
    key: "gem.envMapIntensity", category: "diamond", label: "Brilliance", type: "number", lo: 0.6, hi: 2.55,
    description: "Overall brightness of the light inside the stone. Higher = brighter, whiter stone; lower = darker, moodier stone.",
  },
  {
    key: "gem.internalEnvBoost", category: "diamond", label: "Inner light", type: "number", lo: 0.5, hi: 2,
    description: "Brightness of light returning out through the top of the stone after bouncing inside. Higher = glowing, bright interior.",
  },
  {
    key: "gem.surfaceReflBoost", category: "diamond", label: "Surface shine", type: "number", lo: 0.4, hi: 2.4,
    description: "Strength of the mirror reflection on the stone's outer surface (the table and facet faces).",
  },
  {
    key: "gem.reflectivity", category: "diamond", label: "Surface reflectivity", type: "number", lo: 0.2, hi: 0.8,
    description: "How mirror-like the stone's outer facets are, before the shine multiplier. Higher = more glare on the facets.",
  },
  {
    key: "gem.absorptionFactor", category: "diamond", label: "Body tint depth", type: "number", lo: 0, hi: 2.4,
    description: "How strongly the stone's colour darkens with thickness. 0 = colour barely shows; higher = deeper, saturated body colour (matters for coloured stones).",
  },
  {
    key: "gem.color", category: "diamond", label: "Stone colour", type: "color",
    description: "Body colour of the stone as a hex tint. #ffffff = colourless diamond; pale tints give champagne, yellow, pink or blue diamonds.",
  },
  {
    key: "gem.ior", category: "diamond", label: "Light bending", type: "number", lo: 2.2, hi: 2.75,
    description: "How strongly the stone bends light (refractive index; a real diamond is 2.42). Higher = more light trapped and returned through the top, a denser and more broken-up facet pattern; lower = a glassier stone with larger, simpler facet reflections.",
  },
  {
    key: "gem.envRotation", category: "diamond", label: "Light angle in the stone", type: "number", lo: 0, hi: 5.65487,
    description: "Turns the studio lighting seen inside the stone (radians, a full turn is 6.28). Changes WHERE the bright and dark facets fall without changing overall brightness; use it to move a dark patch off the table.",
  },
  {
    key: "probe.enabled", category: "diamond", label: "Capture the piece's surroundings", type: "boolean",
    description: "Captures the whole piece (metal and stones) once from its centre so stones can reflect it. Needed for the metal to show inside the stones; on its own it changes nothing in the picture.",
  },
  {
    key: "probe.gemReflect", category: "diamond", label: "Metal seen inside stones", type: "boolean",
    description: "Whether the stones reflect the piece around them — the metal of the setting and the neighbouring stones appear inside each stone, as in a real photograph of set stones.",
  },
  {
    key: "probe.gemStrength", category: "diamond", label: "Metal-in-stone strength", type: "number", lo: 0, hi: 1,
    description: "How strongly the surrounding metal shows inside the stones. 0 = stones show only the studio lighting; higher = a warm metal tint and setting reflections visible inside the stones (strongest with yellow or rose gold).",
  },

  // ── Metal (applies to every metal part) ──────────────────────────────────
  {
    key: "metal.metalness", category: "metal", label: "Metallic", type: "number", lo: 0.5, hi: 1,
    description: "1 = pure metal (reflections tinted by the metal colour); lower = plastic/painted look with white highlights.",
  },
  {
    key: "metal.roughness", category: "metal", label: "Satin (less polish)", type: "number", lo: 0, hi: 0.4,
    description: "Surface roughness. 0 = mirror polish with sharp reflections; 0.1–0.2 = satin; 0.3+ = brushed/matte with blurry highlights.",
  },
  {
    key: "metal.envMapIntensity", category: "metal", label: "Metal brightness", type: "number", lo: 0.5, hi: 2.5,
    description: "How bright the metal's reflections of the studio are. Higher = brighter, more reflective metal; lower = darker metal.",
  },
  {
    key: "metal.specularIntensity", category: "metal", label: "Highlight strength", type: "number", lo: 0.4, hi: 1.6,
    description: "Strength of the specular highlight layer. Mostly visible when Metallic is below 1.",
  },
  {
    key: "metal.color", category: "metal", label: "Metal colour", type: "color",
    description: "Metal body colour as hex. Yellow gold ≈ #e5b377, rose gold ≈ #f2ae83, white gold ≈ #c2c2c3, platinum ≈ #b9bbbc, silver ≈ #dedede.",
  },
  {
    key: "metal.clearcoat", category: "metal", label: "Clear coat", type: "number", lo: 0, hi: 1,
    description: "A clear lacquer-like layer over the metal that adds a second, sharp white highlight on top of the metal's own reflection. 0 = bare metal; 1 = a glossy coated look (most visible on satin or rough metal).",
  },
  {
    key: "metal.sparkle", category: "metal", label: "Metal glitter", type: "boolean",
    description: "Whether the metal surface carries tiny glitter flakes that catch the light, like diamond-dust or sandblasted sparkle finishes.",
  },
  {
    key: "metal.sparkleBump", category: "metal", label: "Metal glitter strength", type: "number", lo: 0.0001, hi: 0.001,
    description: "How strongly each glitter flake tilts away from the surface. Low = a faint shimmer; high = bright, scattered points of light across the metal.",
  },
  {
    key: "metal.sparkleSize", category: "metal", label: "Metal glitter coverage", type: "number", lo: 0.2, hi: 0.95,
    description: "How much of the surface the glitter flakes cover (flake radius). Low = sparse pinpoint flakes; high = dense, all-over glitter.",
  },
  {
    key: "metal.sparkleScale", category: "metal", label: "Metal glitter fineness", type: "number", lo: 300, hi: 1500,
    description: "How fine the glitter grain is (flakes per unit, sized for a ring the studio has auto-scaled). Low = coarse, clearly separate flakes like sandblasted or diamond-dust finishes; high = a fine, almost satin shimmer.",
  },

  // ── Reflections (screen-space reflections between parts) ────────────────
  {
    key: "ssr.enabled", category: "reflections", label: "Reflections", type: "boolean",
    description: "Whether parts reflect each other (metal mirroring the diamonds and other metal).",
  },
  {
    key: "ssr.intensity", category: "reflections", label: "Reflection strength", type: "number", lo: 0, hi: 2.5,
    description: "Strength of the reflections parts cast on each other. Higher = stronger, more visible inter-reflections on polished metal.",
  },
  {
    key: "ssr.wMetal", category: "reflections", label: "Metal reflects metal", type: "number", lo: 0.4, hi: 2.2,
    description: "How strongly metal reflects other metal surfaces.",
  },
  {
    key: "ssr.wGem", category: "reflections", label: "Metal reflects stones", type: "number", lo: 0.4, hi: 2.2,
    description: "How strongly metal reflects the diamonds (sparkly reflections in the prongs and band).",
  },

  // ── Ground & shadow ──────────────────────────────────────────────────────
  {
    key: "shadow.enabled", category: "shadow", label: "Ground shadow", type: "boolean",
    description: "Whether the piece casts a soft shadow on the ground under it.",
  },
  {
    key: "shadow.darkness", category: "shadow", label: "Shadow darkness", type: "number", lo: 0.1, hi: 1.5,
    description: "How dark the ground shadow is. Low = faint grey shadow; high = deep, dark contact shadow.",
  },
  {
    key: "shadow.softness", category: "shadow", label: "Shadow softness", type: "number", lo: 0, hi: 1,
    description: "Penumbra size of the ground shadow. 0 = crisp shadow edges; 1 = very soft, diffuse shadow.",
  },
  {
    key: "shadow.blur", category: "shadow", label: "Shadow blur", type: "number", lo: 0, hi: 6,
    description: "Extra blur applied to the ground shadow. Higher = smoother, more cloud-like shadow.",
  },
  {
    key: "shadow.spread", category: "shadow", label: "Shadow spread", type: "number", lo: 0.8, hi: 3,
    description: "How far the shadow can spread across the ground. Higher = a wider shadow halo around the piece.",
  },
  {
    key: "shadow.falloff", category: "shadow", label: "Shadow falloff", type: "number", lo: 0.5, hi: 3,
    description: "How quickly the shadow fades away from the contact point. Higher = shadow fades faster, darkness concentrated right under the piece.",
  },
  {
    key: "floor.reflection", category: "shadow", label: "Floor reflection", type: "boolean",
    description: "Whether the ground shows a mirror reflection of the piece (a glossy tabletop look).",
  },
  {
    key: "floor.reflectionStrength", category: "shadow", label: "Floor reflection strength", type: "number", lo: 0, hi: 1.65,
    description: "Opacity of the piece's reflection in the ground.",
  },
  {
    key: "floor.reflectionBlur", category: "shadow", label: "Floor reflection blur", type: "number", lo: 0.1, hi: 0.9,
    description: "Blur of the ground reflection. Low = sharp glass-like mirror; high = soft, satin floor.",
  },

  // ── Light & background ───────────────────────────────────────────────────
  {
    key: "light.exposure", category: "light", label: "Brightness", type: "number", lo: 0.4, hi: 2.2,
    description: "Overall image brightness (camera exposure). Higher = brighter picture everywhere.",
  },
  {
    key: "light.envIntensity", category: "light", label: "Studio light", type: "number", lo: 0.52, hi: 2,
    description: "Brightness of the studio environment lighting the metal. Higher = brighter metal and highlights without lifting the background.",
  },
  {
    key: "light.contrast", category: "light", label: "Contrast", type: "number", lo: 0.7, hi: 1.5,
    description: "Image contrast. Higher = punchier, deeper blacks and brighter highlights; lower = flatter, softer image.",
  },
  {
    key: "light.saturation", category: "light", label: "Saturation", type: "number", lo: 0.6, hi: 1.4,
    description: "Colour saturation of the whole image. Higher = richer gold and coloured stones; lower = more muted.",
  },
  {
    key: "bloom.enabled", category: "light", label: "Glow", type: "boolean",
    description: "Whether the brightest highlights glow softly (bloom).",
  },
  {
    key: "bloom.intensity", category: "light", label: "Glow strength", type: "number", lo: 0, hi: 0.6,
    description: "Strength of the glow around bright highlights and diamond sparkles.",
  },
  {
    key: "bloom.threshold", category: "light", label: "Glow threshold", type: "number", lo: 0.6, hi: 2.4,
    description: "How bright a pixel must be before it glows. Lower = more of the image glows (dreamier); higher = only the hottest sparkles glow.",
  },
  {
    key: "light.background", category: "light", label: "Background", type: "color",
    description: "Solid background colour behind the piece, as hex. White product-shot = #ffffff.",
  },
];

export const LOOK_FIELD_KEYS: string[] = LOOK_FIELDS.map((f) => f.key);

export function lookField(key: string): LookField | undefined {
  return LOOK_FIELDS.find((f) => f.key === key);
}

/**
 * Clamp/normalise one proposed value against its field. Returns `undefined`
 * when the value cannot be used at all (wrong type, bad hex) — callers drop it.
 * Used on BOTH sides of the wire: the server never forwards an out-of-band
 * value, and the browser never trusts that it did.
 */
export function sanitizeLookValue(field: LookField, value: unknown): number | boolean | string | undefined {
  if (field.type === "boolean") return typeof value === "boolean" ? value : undefined;
  if (field.type === "color") {
    if (typeof value !== "string") return undefined;
    const m = /^#?([0-9a-f]{6})$/i.exec(value.trim());
    return m ? `#${m[1].toLowerCase()}` : undefined;
  }
  if (typeof value !== "number" || !Number.isFinite(value)) return undefined;
  let v = Math.min(field.hi ?? value, Math.max(field.lo ?? value, value));
  if (field.integer) v = Math.round(v);
  return v;
}
